// Purchase batch maths shared by the purchase batches panel and the GRN /
// Purchase Invoice barcode scan, so a batch changed by a scan is priced
// exactly like one edited in the panel.

// Amounts that come before tax. `dis` is the general discount as a fraction
// (10% -> 0.1). `taxablePurchaseRate` is what the tax API is called with.
export const purchaseBatchPreTax = ({
  qty,
  pr = 0,
  dis = 0,
  forQty = 0,
  forFreeQty = 0,
  freeQty = 0,
  schemePer = 0,
}) => {
  const denominator = parseInt(forQty) + parseInt(forFreeQty);

  const item_batch_scheme_discount_percentage = schemePer
    ? schemePer
    : denominator > 0
    ? parseFloat((parseInt(forFreeQty) / denominator) * 100).toFixed(2)
    : 0;

  const item_batch_total_base_purchase_rate =
    parseFloat(pr || 0) * parseFloat(qty || 0);

  const item_batch_scheme_discount_amount =
    parseFloat(
      (item_batch_scheme_discount_percentage / 100) *
        item_batch_total_base_purchase_rate
    ).toFixed(2) || 0;

  const item_batch_scheme_purchase_rate =
    (
      parseFloat(item_batch_total_base_purchase_rate || 0) -
      parseFloat(item_batch_scheme_discount_amount || 0)
    ).toFixed(2) || 0;

  const item_batch_gen_discount_amount =
    parseFloat(dis * item_batch_scheme_purchase_rate).toFixed(2) ?? 0;

  const item_batch_discounted_purchase_rate =
    parseFloat(
      item_batch_scheme_purchase_rate - item_batch_gen_discount_amount
    )?.toFixed(2) || 0;

  const item_batch_total_effective_qauntity =
    parseFloat(qty || 0) + parseFloat(freeQty || 0);

  return {
    item_batch_scheme_discount_percentage,
    item_batch_total_base_purchase_rate,
    item_batch_scheme_discount_amount,
    item_batch_scheme_purchase_rate,
    item_batch_gen_discount_amount,
    item_batch_discounted_purchase_rate,
    item_batch_total_effective_qauntity,
    taxablePurchaseRate:
      item_batch_scheme_purchase_rate -
      item_batch_scheme_purchase_rate * (dis || 0),
  };
};

// The batch once the tax API (purchaseTaxCalAction) has answered.
export const purchaseBatchWithTax = (
  batch,
  inputs,
  preTax,
  taxData = {},
  extraFields = {}
) => {
  const {
    qty,
    pr = 0,
    dis = 0,
    forQty = 0,
    forFreeQty = 0,
    freeQty = 0,
    unitPrice,
  } = inputs;
  const {
    item_tax_amount,
    item_total_tax_percentage,
    item_batch_cgst_percentage,
    item_batch_sgst_percentage,
    item_batch_igst_percentage,
    item_batch_sgst_amount,
    item_batch_cgst_amount,
    item_batch_igst_amount,
  } = taxData;

  const tax = +item_tax_amount || 0;
  const sgst = +item_batch_sgst_amount || 0;
  const cgst = +item_batch_cgst_amount || 0;
  const igst = +item_batch_igst_amount || 0;
  const effectiveQty = preTax.item_batch_total_effective_qauntity;

  const item_batch_total_purchase_rate = parseFloat(
    parseFloat(preTax.item_batch_discounted_purchase_rate) + parseFloat(tax)
  ).toFixed(2);

  const item_batch_final_purchase_rate =
    effectiveQty > 0
      ? parseFloat(item_batch_total_purchase_rate / effectiveQty).toFixed(2)
      : 0;

  const item_batch_margin_percentage =
    item_batch_final_purchase_rate > 0
      ? (
          ((parseFloat(unitPrice || 0) -
            parseFloat(item_batch_final_purchase_rate)) /
            parseFloat(item_batch_final_purchase_rate)) *
          100
        ).toFixed(2)
      : 0;

  return {
    ...batch,
    item_sellable_quantity: +qty || "",
    item_batch_free_quantity: +freeQty || "",
    item_batch_total_effective_qauntity: effectiveQty,
    item_batch_purchase_rate: pr || "",
    item_batch_total_base_purchase_rate:
      preTax.item_batch_total_base_purchase_rate,
    item_batch_for_qauntity: forQty || "",
    item_batch_for_free_qauntity: forFreeQty || "",
    item_batch_scheme_discount_percentage:
      preTax.item_batch_scheme_discount_percentage || "",
    item_batch_scheme_discount_amount:
      preTax.item_batch_scheme_discount_amount || 0,
    item_batch_scheme_purchase_rate:
      preTax.item_batch_scheme_purchase_rate || "",
    item_batch_discount_percentage: dis * 100 || "",
    item_batch_gen_discount_amount: preTax.item_batch_gen_discount_amount,
    item_batch_discounted_purchase_rate:
      preTax.item_batch_discounted_purchase_rate || 0,
    item_batch_discount_amount: (
      parseFloat(preTax.item_batch_gen_discount_amount || 0) +
      parseFloat(preTax.item_batch_scheme_discount_amount || 0)
    ).toFixed(2),
    item_batch_tax_percentage: item_total_tax_percentage,
    item_batch_tax_amount: parseFloat(tax).toFixed(2),
    item_batch_total_purchase_rate: item_batch_total_purchase_rate,
    item_batch_unit_price: unitPrice || "",
    item_batch_final_purchase_rate: item_batch_final_purchase_rate || 0,
    item_batch_margin_percentage: item_batch_margin_percentage,
    item_batch_sgst_amount: parseFloat(sgst).toFixed(2),
    item_batch_cgst_amount: parseFloat(cgst).toFixed(2),
    item_batch_igst_amount: parseFloat(igst).toFixed(2),
    item_batch_cgst_percentage: parseFloat(item_batch_cgst_percentage),
    item_batch_sgst_percentage: parseFloat(item_batch_sgst_percentage),
    item_batch_igst_percentage: parseFloat(item_batch_igst_percentage),
    item_batch_purhcase_rate_before_tax:
      effectiveQty > 0
        ? (
            parseFloat(item_batch_final_purchase_rate || 0) -
            parseFloat(tax).toFixed(2) / effectiveQty
          ).toFixed(2)
        : 0,
    // Converted Pack/Loose/Purchase values from the conversion API
    // override the qty-derived defaults so each field shows its value.
    ...extraFields,
  };
};

// The inputs the panel recalculates a saved batch from
export const purchaseBatchInputs = (batch) => ({
  qty: +batch?.item_sellable_quantity || 0,
  pr: parseFloat(batch?.item_batch_purchase_rate || 0),
  dis: parseFloat(batch?.item_batch_discount_percentage || 0) / 100,
  forQty: parseInt(batch?.item_batch_for_qauntity || 0),
  forFreeQty: parseInt(batch?.item_batch_for_free_qauntity || 0),
  freeQty: parseFloat(batch?.item_batch_free_quantity || 0),
  schemePer: parseFloat(batch?.item_batch_scheme_discount_percentage || 0),
  unitPrice: parseFloat(batch?.item_batch_unit_price || 0),
});

// An item row rebuilt from its batches
export const purchaseRowWithBatches = (row, item_batches, baseQty = 1) => {
  const sum = (key) =>
    item_batches.reduce((total, batch) => total + (parseFloat(batch[key]) || 0), 0);
  const sumQuantity = sum("item_sellable_quantity");
  const totalDiscount = sum("item_batch_discount_amount");
  const totalTax = sum("item_batch_tax_amount");

  return {
    ...row,
    item_batches,
    quantity: sumQuantity,
    item_free_quantity: +sum("item_batch_free_quantity"),
    gort_total_discount: +totalDiscount,
    item_discount_amount: +totalDiscount,
    item_tax_amount: +totalTax?.toFixed(2),
    item_gst: item_batches?.[0]?.item_batch_tax_percentage || 0,
    item_sgst: sum("item_batch_sgst_amount"),
    item_cgst: sum("item_batch_cgst_amount"),
    item_igst: sum("item_batch_igst_amount"),
    item_pack_size: baseQty * sumQuantity,
    item_total_amount: sum("item_batch_total_purchase_rate").toFixed(2),
  };
};

// Document totals for the given item rows, keyed by the form's prefix
export const purchaseDocumentTotals = (rows, prefix) => {
  const sum = (key) =>
    rows.reduce((total, item) => total + (parseFloat(item[key]) || 0), 0);
  const raw_total_amount = sum("item_total_amount");
  const total_tax = sum("item_tax_amount");
  const total_discount = sum("item_discount_amount");

  return {
    [`${prefix}_sub_total`]: (
      raw_total_amount -
      total_tax +
      total_discount
    ).toFixed(2),
    [`${prefix}_total_gst`]: total_tax,
    [`${prefix}_total_discount`]: total_discount,
    [`${prefix}_total_amount`]: Math.round(raw_total_amount).toFixed(2),
    [`${prefix}_round_off_value`]: (
      Math.round(raw_total_amount) - raw_total_amount
    ).toFixed(2),
    [`${prefix}_tcs_or_tds_amount`]: 0,
    [`${prefix}_tcs_or_tds_percentage_id`]: "",
    [`${prefix}_tcs_or_tds_percentage`]: 0,
    [`${prefix}_tds_tcs_type`]: "",
  };
};
